import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './card.css';
import { normalizeConfig, type ConsentColors, type ConsentConfig, type ConsentService } from './types';
import { translations } from './translations';
import { catalogFor } from './service-catalog';
import { setupContentBlocker, syncContentBlocker, EMBED_CATEGORY } from './content-blocker';

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * The language of the currently rendered page (from <html lang>). Each translated
 * page is served in a single language (WPML/Polylang), so this is unambiguous and
 * lets the service cards / content-blocker render in the page's language.
 */
function currentLang(fallback: 'de' | 'en'): 'de' | 'en' {
  const l = (document.documentElement.lang || '').toLowerCase();
  if (l.indexOf('de') === 0) return 'de';
  if (l.indexOf('en') === 0) return 'en';
  return fallback;
}

/**
 * Build the eRecht24-style service "card" used as the CookieConsent service label
 * (HTML is allowed). Per-site fields (description, company, links) come from
 * Borlabs; purposes/technologies/data fall back to the curated catalog.
 * Note: CookieConsent service labels are not per-language, so cards render in the
 * configured default language.
 */
function buildServiceLabel(s: ConsentService, lang: 'de' | 'en'): string {
  const card = translations[lang].card;
  const cat = catalogFor(s.key, lang);
  const purposes = s.purposes ?? cat?.purposes ?? [];
  const technologies = s.technologies ?? cat?.technologies ?? [];
  const data = s.data ?? cat?.data ?? [];

  const chips = (arr: string[]): string =>
    `<div class="occ-chips">${arr.map((x) => `<span class="occ-chip">${escapeHtml(x)}</span>`).join('')}</div>`;
  const row = (title: string, body: string): string =>
    `<div class="occ-card-row"><span class="occ-card-h">${title}</span>${body}</div>`;

  // Prefer the translated catalog description; fall back to the Borlabs text.
  const description = cat?.description ?? s.description;
  const parts: string[] = [];
  if (description) parts.push(row(card.description, `<p>${escapeHtml(description)}</p>`));
  if (s.company) parts.push(row(card.company, `<p>${escapeHtml(s.company)}</p>`));
  const links: string[] = [];
  if (s.privacyUrl) links.push(`<a href="${escapeHtml(s.privacyUrl)}" target="_blank" rel="noopener">${card.privacy}</a>`);
  if (s.cookieUrl) links.push(`<a href="${escapeHtml(s.cookieUrl)}" target="_blank" rel="noopener">${card.cookies}</a>`);
  if (links.length) parts.push(`<div class="occ-card-row">${links.join(' · ')}</div>`);
  if (purposes.length) parts.push(row(card.purposes, chips(purposes)));
  if (technologies.length) parts.push(row(card.technologies, chips(technologies)));
  if (data.length) parts.push(row(card.data, chips(data)));

  let html = `<span class="occ-svc-name">${escapeHtml(s.name)}</span>`;
  if (parts.length) {
    html += `<details class="occ-card"><summary>${card.details}</summary><div class="occ-card-body">${parts.join('')}</div></details>`;
  }
  return html;
}

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

// Mirrors the gtag stub from headSnippet(). If the head bootstrap already ran,
// this pushes to the same dataLayer; if not (e.g. banner loaded standalone), it
// still works.
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/** Push the current accepted-category state into Consent Mode v2 signals. */
function syncConsentMode(): void {
  const granted = (category: string): 'granted' | 'denied' =>
    CookieConsent.acceptedCategory(category) ? 'granted' : 'denied';

  gtag('consent', 'update', {
    analytics_storage: granted('analytics'),
    ad_storage: granted('ads'),
    ad_user_data: granted('ads'),
    ad_personalization: granted('ads'),
    functionality_storage: granted('functional'),
    personalization_storage: granted('functional'),
  });

  // Let GTM triggers react to consent changes. We emit both our own event and
  // `borlabs_consent_saved` so existing Borlabs-built GTM containers keep firing.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'consent_update' });
  window.dataLayer.push({ event: 'borlabs_consent_saved' });
}

/** Sync Consent Mode AND the content blocker — used on every consent event. */
function afterConsent(): void {
  syncConsentMode();
  syncContentBlocker(CookieConsent.acceptedCategory(EMBED_CATEGORY));
}

/** Persist functional consent (e.g. when a user clicks an embed placeholder). */
function acceptFunctional(): void {
  const prefs = CookieConsent.getUserPreferences();
  const accepted: string[] = (prefs && prefs.acceptedCategories) || [];
  if (accepted.indexOf(EMBED_CATEGORY) === -1) {
    CookieConsent.acceptCategory([...accepted, EMBED_CATEGORY]);
  }
}

/** Map brand colors onto CookieConsent v3 CSS custom properties. */
function applyColors(colors: ConsentColors): void {
  const root = document.documentElement;
  const set = (name: string, value: string) => root.style.setProperty(name, value);
  set('--cc-btn-primary-bg', colors.primary);
  set('--cc-btn-primary-border-color', colors.primary);
  set('--cc-btn-primary-hover-bg', colors.accent);
  set('--cc-btn-primary-hover-border-color', colors.accent);
  set('--cc-btn-primary-text', '#ffffff');
  set('--cc-btn-primary-hover-text', '#ffffff');
  set('--cc-toggle-on-bg', colors.secondary);
  set('--cc-cookie-category-block-bg-hover', '#f0f4f8');
}

/**
 * Configure and show the consent banner. Safe to call after DOM is ready.
 * Returns the resolved config (useful for tests/debugging).
 */
export function init(input: Partial<ConsentConfig>): ConsentConfig {
  const config = normalizeConfig(input);
  applyColors(config.colors);
  const pageLang = currentLang(config.lang);

  // Group individual services (migrated from Borlabs) under their category, in the
  // shape CookieConsent v3 expects: { serviceKey: { label } }.
  const servicesByCat: Record<string, Record<string, { label: string }>> = {};
  for (const s of config.services ?? []) {
    if (!servicesByCat[s.category]) servicesByCat[s.category] = {};
    servicesByCat[s.category][s.key] = { label: buildServiceLabel(s, pageLang) };
  }
  const hasSvc = (c: string): boolean => !!servicesByCat[c] && Object.keys(servicesByCat[c]).length > 0;

  // A category appears if its base condition holds OR it lists any service.
  const showAds = config.categories.ads || hasSvc('ads');
  const showFunctional = config.categories.calltracking || !!config.contentBlocker || hasSvc('functional');

  const withServices = (cat: string, base: Record<string, unknown>): Record<string, unknown> =>
    servicesByCat[cat] ? { ...base, services: servicesByCat[cat] } : base;

  const categories: Record<string, unknown> = {
    necessary: withServices('necessary', { enabled: true, readOnly: true }),
    analytics: withServices('analytics', {}),
  };
  if (showAds) categories.ads = withServices('ads', {});
  if (showFunctional) categories.functional = withServices('functional', {});

  // Bundle ALL supported languages and let CookieConsent pick per page from the
  // document's <html lang> (autoDetect: 'document'). On multilingual sites
  // (WPML/Polylang) WordPress serves the correct lang per URL, so the banner
  // follows the page language automatically. `config.lang` is only the fallback.
  const supported: Array<'de' | 'en'> = ['de', 'en'];
  const langTranslations: Record<string, unknown> = {};
  for (const lang of supported) {
    const t = translations[lang];
    // Banner footer: privacy policy + imprint links (always reachable, legally required).
    const linkParts: string[] = [];
    if (config.links?.privacy) {
      linkParts.push(`<a href="${config.links.privacy}" target="_blank" rel="noopener">${t.links.privacy}</a>`);
    }
    if (config.links?.imprint) {
      linkParts.push(`<a href="${config.links.imprint}" target="_blank" rel="noopener">${t.links.imprint}</a>`);
    }
    const footer = linkParts.join(' · ');
    const sections: unknown[] = [
      { title: t.sections.intro.title, description: t.sections.intro.description },
      { title: t.sections.necessary.title, description: t.sections.necessary.description, linkedCategory: 'necessary' },
      { title: t.sections.analytics.title, description: t.sections.analytics.description, linkedCategory: 'analytics' },
    ];
    if (showAds) {
      sections.push({ title: t.sections.ads.title, description: t.sections.ads.description, linkedCategory: 'ads' });
    }
    if (showFunctional) {
      sections.push({ title: t.sections.functional.title, description: t.sections.functional.description, linkedCategory: 'functional' });
    }
    langTranslations[lang] = {
      consentModal: footer ? { ...t.consentModal, footer } : t.consentModal,
      preferencesModal: { ...t.preferencesModal, sections },
    };
  }

  CookieConsent.run({
    guiOptions: {
      consentModal: { layout: 'box wide', position: 'bottom left' },
      preferencesModal: { layout: 'box' },
    },
    categories: categories as never,
    language: {
      default: config.lang,
      autoDetect: 'document',
      translations: langTranslations,
    },
    // onConsent fires on first decision (and on every page load once consent exists),
    // onChange fires when the user later edits preferences. Sync on both.
    onFirstConsent: afterConsent,
    onConsent: afterConsent,
    onChange: afterConsent,
  } as never);

  if (config.contentBlocker) {
    setupContentBlocker(pageLang, { acceptCategory: acceptFunctional });
    syncContentBlocker(CookieConsent.acceptedCategory(EMBED_CATEGORY));
  }

  return config;
}

/** Re-open the preferences modal (wire to a footer "Cookie-Einstellungen" link). */
export function showPreferences(): void {
  CookieConsent.showPreferences();
}
